/* eslint-disable max-len */
import { IonButton } from '@ionic/react';
import styled from 'styled-components';
import { AllieDefaultColors } from '../../../common/color.types';
import { getTextWidth } from '../../../common/utils';
import { AllieSpinner } from '../../ProgressIndicators/Spinner';
import { AllieButtonCorner, AllieButtonSize, AllieButtonType } from './Button.types';

type InternalButtonState = 'initial' | 'active' | 'hover' | 'focus';

const backgroundColor = {
  active: 90,
  hover: 60,
  loading: 70,
  focus: 90,
  initial: 90,
  disabled: 40,
};

const textColor = {
  active: 90,
  hover: 80,
  loading: 70,
  focus: 70,
  initial: 60,
  disabled: 40,
};

const getBackgroundColorByState = (state: InternalButtonState, color: AllieDefaultColors) => {
  if (color === 'light') return 'light-main';
  return `${color}-${backgroundColor[state]}`;
};

const getBackground = (
  state: InternalButtonState,
  allieType: AllieButtonType,
  color: AllieDefaultColors,
  disabled: boolean,
  loading: boolean
) => {
  if (allieType === 'secondary' && color === 'primary' && state === 'hover') return 'primary-5';
  if (allieType !== 'primary') return 'transparent';
  if (disabled) return `${color}-${backgroundColor.disabled}`;
  if (loading) return `${color}-${backgroundColor.loading}`;

  return getBackgroundColorByState(state, color);
};

const getColor = (
  state: InternalButtonState,
  allieType: AllieButtonType,
  color: AllieDefaultColors,
  disabled: boolean,
  loading: boolean
) => {
  if (allieType === 'primary') return 'white';
  if (disabled) return `${color}-${textColor.disabled}`;
  if (loading) return `${color}-${textColor.loading}`;

  return `${color}-${textColor[state]}`;
};

const getBorder = (allieType: AllieButtonType, color?: AllieDefaultColors) => {
  switch (allieType) {
    case 'secondary':
      if (color === 'primary') {
        return 'primary-30';
      } else {
        return 'inherit';
      }
    case 'subtle':
      return 'neutral-30';
    default:
      return 'transparent';
  }
};

const verticalPadding = {
  normal: 18,
  medium: 16,
  small: 12,
  xs: 8,
  none: 0,
};

interface AllieButtonParams {
  allieType: AllieButtonType;
  allieSize: AllieButtonSize;
  corner: AllieButtonCorner;
  allieColor: AllieDefaultColors;
  disabled: boolean;
  loading: boolean;
  shadow: boolean;
  full: boolean;
  fill?: 'clear' | 'outline' | 'solid' | 'default';
}

const setCorner = (corner: AllieButtonCorner) => {
  switch (corner) {
    case 'rounded':
      return '50px';
    case 'rounded-edges':
      return '4px';
    default:
      return '8px';
  }
};
export const StyledButton: any = styled(IonButton)<AllieButtonParams>`
  --background: var(--allie-color-${(props: AllieButtonParams) => getBackground('initial', props.allieType, props.allieColor, props.disabled, props.loading)});
  --background-activated: var(--allie-color-${(props: AllieButtonParams) => getBackground('active', props.allieType, props.allieColor, props.disabled, props.loading)});
  --background-activated-opacity: 1;
  --background-hover: var(--allie-color-${(props: AllieButtonParams) => getBackground('hover', props.allieType, props.allieColor, props.disabled, props.loading)});
  --background-hover-opacity: 1;
  --background-focused: var(--allie-color-${(props: AllieButtonParams) => getBackground('focus', props.allieType, props.allieColor, props.disabled, props.loading)});
  --background-focused-opacity: 1;
  --color: var(--allie-color-${(props: AllieButtonParams) => getColor('initial', props.allieType, props.allieColor, props.disabled, props.loading)});
  --color-activated: var(--allie-color-${(props: AllieButtonParams) => getColor('active', props.allieType, props.allieColor, props.disabled, props.loading)});
  --color-hover: var(--allie-color-${(props: AllieButtonParams) => getColor('hover', props.allieType, props.allieColor, props.disabled, props.loading)});
  --color-focused: var(--allie-color-${(props: AllieButtonParams) => getColor('focus', props.allieType, props.allieColor, props.disabled, props.loading)});
  --border-color: var(--allie-color-${(props: AllieButtonParams) => getBorder(props.allieType)});
  --border-width: 1px;
  --border-style: ${(props: AllieButtonParams) => (props.fill === 'clear' ? 'none' : 'solid')};
  --border-radius: ${(props: AllieButtonParams) => setCorner(props.corner)};
  --box-shadow: ${(props: AllieButtonParams) => (props.shadow ? '0px 3px 10px rgba(0, 0, 0, 0.18)' : 'none')};
  --padding-top: ${(props: AllieButtonParams) => verticalPadding[props.allieSize]}px;
  --padding-bottom: ${(props: AllieButtonParams) => verticalPadding[props.allieSize]}px;
  --padding-start: ${(props: AllieButtonParams) => verticalPadding[props.allieSize]}px;
  --padding-end: ${(props: AllieButtonParams) => verticalPadding[props.allieSize]}px;
  &:focus {outline: solid 3px var(--allie-color-transparent)}
  width: ${(props: AllieButtonParams) => (props.full ? '100%' : 'auto')};
  height: auto;
  text-transform: none;
  letter-spacing: 0;
  &:hover {
    --border-radius: ${(props: AllieButtonParams) => setCorner(props.corner)};
    --border-color: var(--allie-color-${(props: AllieButtonParams) => getBorder(props.allieType, props.allieColor)});
    --background: var(--allie-color-${(props: AllieButtonParams) => getBackground('hover', props.allieType, props.allieColor, props.disabled, props.loading)})
  }
`;

const spinnerSizeMapping: Record<AllieButtonSize, number> = {
  normal: 16,
  medium: 14,
  small: 12,
  xs: 8,
  none: 0,
};

interface StyledButtonSpinnerProps {
  allieSize: AllieButtonSize;
  children: any;
}

export const StyledButtonSpinner = styled(AllieSpinner)<StyledButtonSpinnerProps>`
  height: ${(props: StyledButtonSpinnerProps) => spinnerSizeMapping[props.allieSize] * 1.1}px;
  width: ${(props: StyledButtonSpinnerProps) =>
    getTextWidth(props.children, spinnerSizeMapping[props.allieSize]) * 1.1}px;
`;
